import { Grid } from "@mui/material";
import { FormikErrors } from "formik";
import React from "react";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../components/formsComponents";
import {
  LoadState,
} from "../../../../../../../constants/enums";
import { InsurerCodeAutoSearch } from "../../../../../../../components/AutoCompleteSearches";

import { ComissionCodeSelect } from "../../../../../../../components/AutoCompleteSearches/ComissionCodeSelect";
import { UsersAutoSearchByRole } from "../../../../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { ISelectOption } from "../../../../../../../constants/types";
import { IPolicyCommission } from "../../../../../../../redux/policyCommission/policyCommission.types";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { PolicyFormSkeleton } from "../../../PoliciesCommon/PolicyFormSkeleton/PolicyFormSkeleton";
import { ICustomerPolicyDetails } from "../../../../../../../redux/customerPolicyDetails/customerPolicyDetails.types";

export interface IMergedFormik {
  values: ICustomerPolicyDetails;
  errors: any;
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T_1 = string | React.ChangeEvent<any>>(
      field: T_1
    ): T_1 extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
  setFieldValue: (field: string, value: any) => void;
  setValues: (
    values: React.SetStateAction<ICustomerPolicyDetails>,
    shouldValidate?: boolean
  ) => Promise<void> | Promise<FormikErrors<ICustomerPolicyDetails>>;
}

interface ILifeInsurancePolicyAdminProps {
  customerpolicyId?: number | null;
  // Both props now receive the same merged Formik instance carrying the
  // canonical ICustomerPolicyDetails values (customer info + admin + premium).
  policyAdmin: IMergedFormik;
  policyPremimum: IMergedFormik;
}

export const LifeInsurancePolicyAdmin: React.FC<
  ILifeInsurancePolicyAdminProps
> = (props) => {
  const { customerpolicyId, policyAdmin, policyPremimum } = props;
  // Admin and premium data now arrive through the merged Formik (initialized by
  // the parent via useCustomerPolicyDetails). The loading state is handled by
  // the parent hook, so this subcomponent renders the form directly. The values
  // are kept as a widened LoadState (not the narrowed literal) so the skeleton
  // branches below type-check cleanly.
  const loading = LoadState.Loaded as LoadState;
  const premiumDataLoading = LoadState.Loaded as LoadState;
  const isProducerDisabled = false;

  const handleInsurer = async (value: ISelectOption) => {
    policyAdmin.setValues({
      ...policyAdmin.values,
      insurer: value.value as string,
      commission_code: null,
      commission_percentage: null,
    });
  };

  const handleCommissionCode = (data: IPolicyCommission | null) => {
    if (data) {
      policyAdmin.setValues({
        ...policyAdmin.values,
        commission_code: data.policy_code,
        commission_percentage: data.commission_new,
      });
    } else {
      policyAdmin.setValues({
        ...policyAdmin.values,
        commission_code: null,
        commission_percentage: null,
      });
    }
  };

  // Admin defaults (branch/csr/marketer) for a new policy are seeded by the
  // owning CustomerInfo Formik initialization effect. Seeding here as well would
  // race with that effect on the same shared Formik and wipe the freshly-seeded
  // values, so it is intentionally not done here.

  return (
    <>
    <LabelCard heading="Policy Admin" />
    {loading === LoadState.InProgress ? (
        <>
          <PolicyFormSkeleton />
        </>
      ) : (
            <>
            <Grid container spacing={1}>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Insurer</CustomFormLabel>
                <InsurerCodeAutoSearch
                  value={policyAdmin.values.insurer}
                  onSelect={handleInsurer}
                />
              </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Comission Code</CustomFormLabel>
            <ComissionCodeSelect
              insurerCode={policyAdmin.values.insurer}
              value={policyAdmin.values.commission_code}
              onChange={handleCommissionCode}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Commission %</CustomFormLabel>
            <CustomTextField
              fullWidth
              value={policyAdmin.values.commission_percentage}
              disabled
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>AGA</CustomFormLabel>
            <CustomTextField
              name="aga"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              disabled
              value={policyAdmin.values.aga}
              onChange={policyAdmin.handleChange}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>AGA Code</CustomFormLabel>
            <CustomTextField
              name="branch_code"
              fullWidth
              value={policyAdmin.values.branch_code}
              disabled
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              label="Advisor 1"
              value={policyAdmin.values.producer_one}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                policyAdmin.setValues({
                  ...policyAdmin.values,
                  producer_one: data.user_fact_id as any,
                  producer_one_code: data.user_code,
                });
              }}
              errorMessage={policyAdmin.errors.producer_one}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              label="Advisor 2"
              value={policyAdmin.values.producer_two}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                policyAdmin.setValues({
                  ...policyAdmin.values,
                  producer_two: data.user_fact_id as any,
                  producer_two_code: data.user_code,
                });
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              label="Advisor 3"
              value={policyAdmin.values.producer_three}
              disabled={isProducerDisabled}
              onSelect={(data) => {
                policyAdmin.setValues({
                  ...policyAdmin.values,
                  producer_three: data.user_fact_id as any,
                  producer_three_code: data.user_code,
                });
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Previous Producer Code</CustomFormLabel>
            <CustomTextField
              name="previous_producer_code"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyAdmin.values.previous_producer_code}
              disabled
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <UsersAutoSearchByRole
              value={policyAdmin.values.csr}
              label="CSR"
              onSelect={(value) => {
                policyAdmin.setValues({
                  ...policyAdmin.values,
                  csr: value.user_fact_id as any,
                  csr_code: value.user_code,
                });
              }}
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Status</CustomFormLabel>
            <ControlledCustomSelect
              name="status"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              displayEmpty
              placeholder="Select One"
              options={[
                { label: "Active", value: "ACTIVE" },
                { label: "Inactive", value: "INACTIVE" },
              ].map((data) => {
                return { label: data.label, value: data.value };
              })}
              value={policyAdmin.values.status}
              onChange={policyAdmin.handleChange}
            />
          </Grid>
        </Grid>
        </>
        )}
      <LabelCard heading="Premium" />
      {premiumDataLoading === LoadState.InProgress ? (
        <PolicyFormSkeleton />
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Policy Premium</CustomFormLabel>
            <CustomTextField
              name="premium"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyPremimum.values.premium}
              onChange={policyPremimum.handleChange}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Payment Plan</CustomFormLabel>
            <CustomTextField
              name="payment_plan"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyPremimum.values.payment_plan}
              onChange={policyPremimum.handleChange}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Commission Code (%)</CustomFormLabel>
            <CustomTextField
              name="commission_code_percentage"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyPremimum.values.commission_code_percentage}
              disabled
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Bonus %</CustomFormLabel>
            <CustomTextField
              name="bonus_percent"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyPremimum.values.bonus_percent}
              disabled
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Bonus Amount</CustomFormLabel>
            <CustomTextField
              name="bonus_amount"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyPremimum.values.bonus_amount}
              disabled
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>FYC</CustomFormLabel>
            <CustomTextField
              name="fyc"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={policyPremimum.values.fyc}
              onChange={policyPremimum.handleChange}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <CustomFormLabel>Status</CustomFormLabel>
            <ControlledCustomSelect
              name="status"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              displayEmpty
              placeholder="Select One"
              options={[
                { label: "Active", value: "ACTIVE" },
                { label: "Inactive", value: "INACTIVE" },
              ].map((data) => {
                return { label: data.label, value: data.value };
              })}
              value={policyPremimum.values.status}
              onChange={policyPremimum.handleChange}
            />
          </Grid>
        </Grid>
        
        )}
    </>
  );
};
